/**
 * The Overview for somebody the Overview is not about.
 *
 * A subcontractor holds none of the three reads the Overview is made of — the
 * Pulse, the lifecycle, the milestones — and on 2026-09-04 the owner signed in
 * as one and saw five boxes in a row each saying "your role does not include".
 * It read as a broken page. This is what they get instead: the counts of THEIR
 * work on this job, each one a link to the register it counts.
 *
 * Every figure is the TOTAL the server reports for this reader, which the
 * registers already scope to their packages and to what is assigned to them —
 * this screen never counts anything itself, so it cannot disagree with the
 * register it links to. A register the server refuses is left out rather than
 * shown as "none": a refusal is not a count of zero.
 */

import { Fragment, useEffect, useState } from 'react';

import {
  ApiError,
  listActivities,
  listDeliverables,
  listQuality,
  listSnags,
  listSubmittals,
  listTasks,
} from '../api.js';
import { CrossLink } from './cross-links.js';
import type { SectionKey } from './project-sections.js';

interface Figure {
  readonly key: string;
  readonly label: string;
  readonly count: number;
  readonly section: SectionKey;
}

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly figures: readonly Figure[] }
  | { readonly status: 'failed'; readonly message: string };

/** `null` for a refusal — the figure is left out — and a throw for anything else. */
async function unlessRefused<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read();
  } catch (caught) {
    if (caught instanceof ApiError && (caught.status === 403 || caught.status === 404)) return null;
    throw caught;
  }
}

export function MyWorkOnThisJob({ projectId }: { readonly projectId: string }): JSX.Element {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    void (async () => {
      try {
        const [tasks, snags, quality, submittals, drawings, activities] = await Promise.all([
          unlessRefused(() => listTasks(projectId)),
          unlessRefused(() => listSnags(projectId)),
          unlessRefused(() => listQuality(projectId)),
          unlessRefused(() => listSubmittals(projectId)),
          unlessRefused(() => listDeliverables(projectId)),
          unlessRefused(() => listActivities(projectId)),
        ]);
        if (cancelled) return;

        const figures: Figure[] = [];
        if (tasks) figures.push({ key: 'tasks', label: 'Open tasks', count: tasks.total, section: 'site' });
        if (snags) figures.push({ key: 'snags', label: 'Snags', count: snags.total, section: 'quality' });
        if (quality) {
          figures.push({
            key: 'inspections',
            label: 'Inspections',
            count: quality.inspections_total,
            section: 'quality',
          });
          figures.push({ key: 'ncrs', label: 'NCRs', count: quality.ncrs_total, section: 'quality' });
        }
        if (submittals) {
          figures.push({ key: 'submittals', label: 'Submittals', count: submittals.total, section: 'design' });
        }
        if (drawings) figures.push({ key: 'drawings', label: 'Drawings', count: drawings.total, section: 'design' });
        if (activities) {
          figures.push({
            key: 'activities',
            label: 'Programme activities',
            count: activities.total,
            section: 'site',
          });
        }
        setState({ status: 'ready', figures });
      } catch (caught) {
        if (cancelled) return;
        setState({
          status: 'failed',
          message:
            caught instanceof ApiError ? caught.message : 'Your work on this job could not be loaded.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Your work on this job</h3>
    </div>
  );

  if (state.status === 'loading') {
    return (
      <>
        {head}
        <p aria-live="polite">Loading your work…</p>
      </>
    );
  }

  if (state.status === 'failed') {
    return (
      <>
        {head}
        <p role="alert">{state.message}</p>
      </>
    );
  }

  return (
    <>
      {head}
      <p className="hint">
        Counted here: what is in your work packages, and what is given to you by name. Nothing
        else on this job is yours to see, so nothing else is counted.
      </p>
      {state.figures.length === 0 ? (
        <p className="hint">Your role does not include any of this project&rsquo;s registers.</p>
      ) : (
        <dl className="detail-grid">
          {state.figures.map((figure) => (
            <Fragment key={figure.key}>
              <dt>{figure.label}</dt>
              <dd>
                <CrossLink
                  projectId={projectId}
                  section={figure.section}
                  says={`open the ${figure.label.toLowerCase()} on this job`}
                >
                  {figure.count === 0 ? 'none' : String(figure.count)}
                </CrossLink>
              </dd>
            </Fragment>
          ))}
        </dl>
      )}
    </>
  );
}
